import * as React from "react"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

import { useImageCaption } from "@/tiptap/ui/image-caption-button"

import { Button } from "@/tiptap/ui-primitive/button"

/**
 * Button component for toggling image captions in a Tiptap editor.
 * Only appears when an image is selected in the editor.
 *
 * For custom button implementations, use the `useImageCaption` hook instead.
 */
export const ImageCaptionButton = React.forwardRef((
  {
    editor: providedEditor,
    text,
    hideWhenUnavailable = false,
    onSet,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const { isVisible, isActive, canToggle, handleToggleCaption, label, Icon } =
    useImageCaption({
      editor,
      hideWhenUnavailable,
      onSet,
    })

  const handleClick = React.useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleToggleCaption()
  }, [handleToggleCaption, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type="button"
      variant="ghost"
      data-active-state={isActive ? "on" : "off"}
      role="button"
      tabIndex={-1}
      disabled={!canToggle}
      data-disabled={!canToggle}
      aria-label={label}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <Icon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
        </>
      )}
    </Button>
  );
})

ImageCaptionButton.displayName = "ImageCaptionButton"
